import { z } from "zod";

import { permalinkPath } from "../dtos/operator/permalink-paths";
import type { BlogCategoryPostCardDto } from "../dtos/traveler/blog-category.dto";

import { readIdListUpTo } from "./category-cards";
import {
  CAROUSEL_GAPS,
  DEFAULT_GAP,
  type CarouselGap,
} from "./carousel-settings";

/**
 * The Blog Posts block — a grid of the agency's published posts.
 *
 * Built like the Tours block (`tour-cards.ts`): one data node carries this
 * type and every setting. `source`, `categories`, `posts` and `limit` are the
 * QUERY, answered by traveler-api before the renderer runs
 * (`content-binding.ts`). The five show switches and the layout settings are
 * render decisions and stay out of it, so two blocks that differ only in what
 * their cards show are one read.
 */
export const BLOG_POSTS_TYPE = "blog-posts";

/** One row of three, as the prototype draws it. */
export const DEFAULT_BLOG_POSTS_LIMIT = 3;

/**
 * The cap on How many, and on a hand-picked list. Every card rides the page's
 * cached payload, so the cap is a payload budget.
 */
export const MAX_BLOG_POSTS_LIMIT = 30;

/**
 * Where a block's posts come from, the Tours block's "Tours from" without
 * its booking feed. `latest` is the newest posts. `category` is the newest
 * posts filed under ANY picked category. `choose` is a hand-picked list, in
 * the order picked. `latest` is the default, so a block dropped and never
 * opened still shows something.
 */
export const BLOG_POST_SOURCES = ["latest", "category", "choose"] as const;
export type BlogPostSource = (typeof BLOG_POST_SOURCES)[number];

const readPostIds = readIdListUpTo(MAX_BLOG_POSTS_LIMIT);

/**
 * Traits are strings, so `limit` arrives as `"3"` and is coerced here. The
 * picks are comma lists of ids (`readIdListUpTo`): a rename never empties a
 * block, and anything that is not a uuid is dropped before it reaches
 * Postgres.
 */
export const blogPostsQuerySchema = z.object({
  source: z.enum(BLOG_POST_SOURCES).catch("latest"),
  categories: z.unknown().optional().transform(readPostIds),
  posts: z.unknown().optional().transform(readPostIds),
  limit: z.coerce
    .number()
    .int()
    .min(1)
    .max(MAX_BLOG_POSTS_LIMIT)
    .catch(DEFAULT_BLOG_POSTS_LIMIT)
    .default(DEFAULT_BLOG_POSTS_LIMIT),
});

export type BlogPostsQuery = z.infer<typeof blogPostsQuerySchema>;

/**
 * One card: the blog page's own card, so the block and `/blog/` can never
 * disagree about a post. `readMinutes` is 220 words a minute, never under one.
 */
export type BlogPostCard = BlogCategoryPostCardDto;

/**
 * A stable identity for a query, so two blocks asking the same thing are one
 * read. Built per source, so picks left over from another source never split
 * the key.
 */
export const blogPostsQueryKey = (query: BlogPostsQuery): string => {
  const base = [BLOG_POSTS_TYPE, query.source];
  if (query.source === "choose") return [...base, ...query.posts].join("|");
  if (query.source === "category") {
    return [...base, ...query.categories, String(query.limit)].join("|");
  }
  return [...base, String(query.limit)].join("|");
};

/** The post a card links to. Posts live at the site root. */
export const blogPostCardPath = (card: Pick<BlogPostCard, "slug">): string =>
  `/${card.slug}/`;

/**
 * The blog category page a card's category links to, under the DEFAULT word
 * (`/category/{slug}/`). The block has no agency's permalink words, and an
 * agency that renamed the word gets a permanent redirect from the default
 * one (permalinks AC-03) — the same arrangement as the Tours card's
 * destination links (`tourCardDestinationPath`).
 */
export const blogPostCategoryPath = (
  category: Pick<BlogPostCard["categories"][number], "slug">,
): string => permalinkPath(undefined, "postCategory", category.slug);

/** The details a card can show, each its own switch, all on by default. */
export const BLOG_POST_DETAILS = [
  "showCategory",
  "showExcerpt",
  "showAuthor",
  "showDate",
  "showReadTime",
] as const;

export type BlogPostDetail = (typeof BLOG_POST_DETAILS)[number];

/**
 * A detail switch. Unset reads as ON, the prototype's default; the one
 * spelling a round-trip produces for "off" is `false`.
 */
export const readBlogPostDetail = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() !== "false";
};

export const BLOG_POSTS_COLUMNS = [2, 3, 4] as const;
export type BlogPostsColumns = (typeof BLOG_POSTS_COLUMNS)[number];

/** Desktop columns. The storefront hands a number and the canvas a string. */
export const readBlogPostsColumns = (value: unknown): BlogPostsColumns =>
  BLOG_POSTS_COLUMNS.find((n) => String(n) === String(value)) ?? 3;

export const BLOG_POSTS_TABLET_COLUMNS = [1, 2, 3] as const;
export type BlogPostsTabletColumns = (typeof BLOG_POSTS_TABLET_COLUMNS)[number];

/** Tablet columns (`columns-tablet`, `device-attrs.ts`). Unset draws 2. */
export const readBlogPostsTabletColumns = (
  value: unknown,
): BlogPostsTabletColumns =>
  BLOG_POSTS_TABLET_COLUMNS.find((n) => String(n) === String(value)) ?? 2;

export const BLOG_POSTS_MOBILE_COLUMNS = [1, 2] as const;
export type BlogPostsMobileColumns = (typeof BLOG_POSTS_MOBILE_COLUMNS)[number];

/** Phone columns (`columns-mobile`), read only while the cards stack. Unset draws 1. */
export const readBlogPostsMobileColumns = (
  value: unknown,
): BlogPostsMobileColumns =>
  BLOG_POSTS_MOBILE_COLUMNS.find((n) => String(n) === String(value)) ?? 1;

/**
 * How the cards sit on a phone, the Tours block's switch (`TOUR_CARD_MOBILE`):
 * `stack` is the grid, `scroll` one row that scrolls sideways and snaps to
 * each card. `scroll` is the default, as on the Tours block.
 */
export const BLOG_POSTS_MOBILE = ["stack", "scroll"] as const;
export type BlogPostsMobile = (typeof BLOG_POSTS_MOBILE)[number];

export const readBlogPostsMobile = (value: unknown): BlogPostsMobile =>
  BLOG_POSTS_MOBILE.find((mobile) => mobile === value) ?? "scroll";

/** The Tours and Categories blocks' Gap words. Unknown reads as `medium`. */
export const readBlogPostsGap = (value: unknown): CarouselGap =>
  CAROUSEL_GAPS.find((gap) => gap === value) ?? DEFAULT_GAP;
